"use client";

import React, { useState } from 'react';
import Link from 'next/link';

const MethodologySection = () => {
  const [activePhase, setActivePhase] = useState(1);

  const phases = [
    {
      number: 1,
      title: "Discover",
      duration: "1-2 weeks",
      whatWeDo: "Deep dive into your current data landscape, pain points, and business objectives",
      keyDeliverables: [
        "Data source inventory",
        "Pain point analysis",
        "EDW roadmap"
      ]
    },
    {
      number: 2,
      title: "Architect",
      duration: "2-4 weeks",
      whatWeDo: "Design data models, Select technology stack, Plan security framework, Define governance rules",
      keyDeliverables: [
        "Technical Blueprint",
        "Security Architecture",
        "Data Governance Policy",
        "Architecture Sign-Off"
      ]
    },
    {
      number: 3,
      title: "Build",
      duration: "8-16 weeks",
      whatWeDo: "Develop ETL pipelines, Build data warehouse, Implement quality checks, Load historical data",
      keyDeliverables: [
        "Production EDW",
        "Automated Data Flows",
        "Quality Assurance Reports",
        "Performance Benchmarks"
      ]
    },
    {
      number: 4,
      title: "Visualize",
      duration: "4-8 weeks",
      whatWeDo: "Build executive dashboards, Create operational reports, Enable self-service BI, Train your teams",
      keyDeliverables: [
        "C-Suite Dashboards",
        "Departmental Reports",
        "User Training",
        "Documentation"
      ]
    },
    {
      number: 5,
      title: "Evolve",
      duration: "Ongoing",
      whatWeDo: "Monitor performance, Add new data sources, Optimize queries, Provide support",
      keyDeliverables: [
        "24/7 Support",
        "Monthly Health Reports",
        "Continuous Optimization",
        "Dedicated Account Manager"
      ]
    }
  ];

  const currentPhase = phases?.find(p => p?.number === activePhase) || phases?.[0];

  return (
    <section className="bg-[#1B2845] py-12 sm:py-16 md:py-24 min-h-0 flex items-center relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>
      <div className="container mx-auto px-4 sm:px-6 max-w-7xl relative z-10">
        {/* Header */}
        <div className="text-left md:text-center mb-8 sm:mb-12 md:mb-16">
          <p className="text-[#C5A572] text-xs sm:text-[14px] tracking-[0.2em] uppercase mb-3 sm:mb-4 font-semibold">
            OUR METHODOLOGY
          </p>
          <h2 className="font-serif text-white text-[28px] sm:text-[34px] md:text-[42px] lg:text-[48px] leading-[1.2] font-normal mb-4 sm:mb-6">
            The Simax 5-Phase Methodology
          </h2>
          <p className="text-white/70 text-[15px] sm:text-[17px] md:text-[20px] max-w-3xl md:mx-auto font-light px-0">
            No surprises. No scope creep. Just transparent, phased execution.
          </p>
        </div>

        {/* Horizontal Timeline Navigation - scroll on mobile */}
        <div className="mb-8 sm:mb-12 md:mb-20 -mx-4 sm:mx-0">
          <div className="flex items-stretch justify-start sm:justify-between gap-4 sm:gap-0 max-w-5xl mx-auto relative overflow-x-auto overflow-y-hidden pb-2 sm:pb-0 px-4 sm:px-0">
            {/* Connecting lines - hidden on mobile (scroll layout) */}
            <div className="hidden sm:block absolute top-[26px] left-0 right-0 flex items-center justify-between px-[22px] md:px-[30px]">
              {[1, 2, 3, 4]?.map((i) => (
                <div 
                  key={i} 
                  className="flex-1 h-px bg-white/20"
                  style={{ marginLeft: i === 1 ? '22px' : '0', marginRight: '22px' }}
                ></div>
              ))}
            </div>

            {/* Phase buttons - min width so they don't shrink when scrolling */}
            {phases?.map((phase) => (
              <div key={phase?.number} className="flex flex-col items-center relative z-10 flex-shrink-0">
                <button
                  onClick={() => setActivePhase(phase?.number)}
                  className={`w-[48px] h-[48px] sm:w-[56px] sm:h-[56px] md:w-[60px] md:h-[60px] rounded-full flex items-center justify-center text-[18px] sm:text-[22px] md:text-[24px] font-bold transition-all duration-300 touch-manipulation ${
                    activePhase === phase?.number
                      ? 'bg-[#C5A572] text-[#1B2845] shadow-lg shadow-[#C5A572]/40'
                      : 'bg-[#2A3F5F] text-white/60 hover:bg-[#3A4F6F] hover:text-white/80 active:bg-[#3A4F6F]'
                  }`}
                >
                  {phase?.number}
                </button>
                <p className={`mt-2 sm:mt-4 text-[13px] sm:text-[16px] font-medium transition-colors duration-300 whitespace-nowrap ${
                  activePhase === phase?.number
                    ? 'text-[#C5A572]'
                    : 'text-white/50'
                }`}>
                  {phase?.title}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Dynamic Phase Content */}
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-br from-[#2A3F5F]/60 to-[#1B2845]/40 backdrop-blur-sm border border-white/10 rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-10 lg:p-12 transition-all duration-500">
            {/* Phase Header */}
            <div className="flex items-start gap-4 sm:gap-6 mb-6 sm:mb-8 md:mb-10">
              <div className="flex-shrink-0">
                <div className="w-[56px] h-[56px] sm:w-[64px] sm:h-[64px] md:w-[80px] md:h-[80px] rounded-full bg-[#C5A572] flex items-center justify-center">
                  <span className="text-[#1B2845] text-[24px] sm:text-[28px] md:text-[36px] font-bold">{currentPhase?.number}</span>
                </div>
              </div>
              <div className="min-w-0">
                <h3 className="text-white text-[24px] sm:text-[30px] md:text-[38px] lg:text-[42px] font-normal mb-1 sm:mb-2 font-serif">
                  {currentPhase?.title}
                </h3>
                <p className="text-[#C5A572] text-[14px] sm:text-[16px] font-medium">
                  {currentPhase?.duration}
                </p>
              </div>
            </div>

            {/* Two Column Content */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 md:gap-10">
              {/* Left Column - What We Do */}
              <div>
                <div className="flex items-center gap-2 mb-6">
                  <svg className="w-5 h-5 text-[#C5A572]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h4 className="text-white text-[14px] font-bold tracking-[0.15em] uppercase">
                    WHAT WE DO
                  </h4>
                </div>
                <p className="text-white/80 text-[16px] leading-[1.8]">
                  {currentPhase?.whatWeDo}
                </p>
              </div>

              {/* Right Column - Key Deliverables */}
              <div>
                <div className="flex items-center gap-2 mb-6">
                  <svg className="w-5 h-5 text-[#C5A572]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h4 className="text-white text-[14px] font-bold tracking-[0.15em] uppercase">
                    KEY DELIVERABLES
                  </h4>
                </div>
                <ul className="space-y-3">
                  {currentPhase?.keyDeliverables?.map((item, i) => (
                    <li key={i} className="text-white/80 text-[16px] leading-[1.8] flex items-start">
                      <svg className="w-5 h-5 text-[#C5A572] mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Additional Info Section */}
            <div className="mt-6 sm:mt-8 md:mt-10 pt-6 sm:pt-8 border-t border-white/10">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#C5A572]/20 flex items-center justify-center mt-1">
                  <svg className="w-4 h-4 text-[#C5A572]" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="text-white/60 text-[14px] leading-[1.7] italic">
                  Comprehensive audit report with actionable recommendations
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA Section */}
        <div className="mt-12 sm:mt-16 md:mt-20 text-left md:text-center px-0">
          <div className="max-w-4xl md:mx-auto mb-8 sm:mb-12">
            <div className="flex items-center justify-start md:justify-center mb-4 sm:mb-6">
              <div className="h-[1px] w-20 bg-gradient-to-r from-transparent via-[#C5A572] to-transparent"></div>
            </div>
            <p className="text-white/95 text-[16px] sm:text-[18px] md:text-[24px] leading-[1.7] font-light tracking-wide mb-2">
              Every phase has <span className="text-[#C5A572] font-normal">defined deliverables</span>.
            </p>
            <p className="text-[16px] sm:text-[18px] md:text-[24px] leading-[1.7] font-light tracking-wide mb-2 text-[rgba(187,159,112,1)]">
              You approve before we proceed. No black boxes. No uncertainty.
            </p>
            <div className="flex items-center justify-start md:justify-center mt-4 sm:mt-6">
              <div className="h-[1px] w-20 bg-gradient-to-r from-transparent via-[#C5A572] to-transparent"></div>
            </div>
          </div>
          <Link
            href="/contact"
            className="group relative inline-flex w-auto min-h-[48px] bg-gradient-to-r from-[#C5A572] to-[#8B7355] text-white px-6 sm:px-12 py-4 sm:py-5 rounded-lg font-semibold text-[14px] sm:text-[16px] md:text-[18px] tracking-wide transition-all duration-500 hover:shadow-2xl hover:shadow-[#C5A572]/40 active:scale-[0.98] overflow-hidden"
          >
            <span className="absolute inset-0 bg-white/10 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></span>
            <span className="relative flex items-center justify-center gap-2 sm:gap-3">
              <span>Request Detailed Process Walkthrough</span>
              <span className="transform group-hover:translate-x-2 transition-transform duration-300">→</span>
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default MethodologySection;